import * as THREE from 'three';
import { resolveRingItemAngle } from '../../_pure/ringLayout.js';
import { resolveEffectiveEntityShape } from './effectiveEntityShape.js';
import { resolveSpokeItemOffsets } from './spokeLayout.js';
function getConstellationLayout(constellation, context) {
    return context.entityConstellationParametricStateByKey?.[constellation.constellationKey]?.layout;
}
function getItemShape(item, context) {
    return context.itemParametricStateByKey?.[item.itemKey]?.shape;
}
function clampAngularGapFraction(value) {
    return Math.min(Math.max(value ?? 0, 0), 0.95);
}
function toTuple(vector) {
    return [vector.x, vector.y, vector.z];
}
function normalizeDirection(direction) {
    const [dx, dy, dz] = direction;
    const vec = new THREE.Vector3(dx, dy, dz);
    if (vec.lengthSq() < 1e-12) {
        vec.set(1, 0, 0);
    }
    else {
        vec.normalize();
    }
    return vec;
}
function findEntityItemEntry(itemKey, context) {
    for (const constellation of context.entityConstellations) {
        const items = context.entityItemsByCollectionKey.get(constellation.itemCollectionKey) ?? [];
        const itemIndex = items.findIndex((item) => item.itemKey === itemKey);
        if (itemIndex < 0)
            continue;
        const item = items[itemIndex];
        if (!item)
            return null;
        return {
            constellation,
            items,
            item,
            itemIndex,
        };
    }
    return null;
}
function resolveSectorSliceLocalAnchor(args) {
    const { anchorKey, outerRadius, innerRadius, itemCount, angularGapFraction } = args;
    const slotSpan = itemCount <= 1 ? Math.PI * 2 : (Math.PI * 2) / itemCount;
    const span = slotSpan * (1 - clampAngularGapFraction(angularGapFraction));
    const midRadius = (innerRadius + outerRadius) / 2;
    const startAngle = -span / 2;
    const endAngle = span / 2;
    switch (anchorKey) {
        case 'center':
            return new THREE.Vector3(0, 0, midRadius);
        case 'arcMid':
            return new THREE.Vector3(0, 0, outerRadius);
        case 'innerArcMid':
            return new THREE.Vector3(0, 0, innerRadius);
        case 'startRadialMid':
            return new THREE.Vector3(Math.sin(startAngle) * midRadius, 0, Math.cos(startAngle) * midRadius);
        case 'endRadialMid':
            return new THREE.Vector3(Math.sin(endAngle) * midRadius, 0, Math.cos(endAngle) * midRadius);
        default:
            return null;
    }
}
function resolveItemLocalAnchor(args) {
    const { item, constellation, itemCount, anchorKey, context } = args;
    const layout = getConstellationLayout(constellation, context);
    const shape = resolveEffectiveEntityShape({
        layout,
        itemParametricState: context.itemParametricStateByKey?.[item.itemKey],
        itemCount,
    });
    if (shape) {
        const directAnchor = shape.anchorsByKey[anchorKey];
        if (directAnchor) {
            const [x, y, z] = directAnchor.localPosition;
            return new THREE.Vector3(x, y, z);
        }
    }
    return null;
}
function resolveRingItemTransform(entry, context) {
    const { constellation, items, itemIndex } = entry;
    const layout = getConstellationLayout(constellation, context);
    if (layout?.layoutKind !== 'ring')
        return null;
    const center = resolveLayoutAnchorWorldPosition({ anchor: layout.anchor, context });
    if (center === null)
        return null;
    const itemCount = items.length;
    if (itemCount === 0)
        return null;
    const angle = resolveRingItemAngle({
        layout,
        itemKey: entry.item.itemKey,
        itemIndex,
        itemCount,
    });
    const rotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(...(layout.rotation ?? [0, 0, 0])));
    const itemState = context.itemParametricStateByKey?.[entry.item.itemKey];
    const facing = layout.panelFacing && itemState && 'shape' in itemState && itemState.shape?.shapeKind === 'panel'
        ? (layout.panelFacing === 'inward' ? -1 : 1) : 0;
    const yaw = facing ? Math.atan2(facing * Math.cos(angle), facing * Math.sin(angle)) : 0;
    return {
        position: new THREE.Vector3(Math.cos(angle) * layout.radius, 0, Math.sin(angle) * layout.radius).applyQuaternion(rotation).add(new THREE.Vector3(...center)),
        rotation: rotation.clone().multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(0, yaw, 0))),
    };
}
function resolveSectorRingItemTransform(entry, context) {
    const { constellation, items, itemIndex } = entry;
    const layout = getConstellationLayout(constellation, context);
    if (layout?.layoutKind !== 'sector-ring')
        return null;
    const itemCount = items.length;
    if (itemCount === 0)
        return null;
    const layoutPosition = resolveLayoutAnchorWorldPosition({
        anchor: layout.anchor,
        context,
    });
    if (layoutPosition === null)
        return null;
    const angle = (itemIndex / itemCount) * Math.PI * 2;
    const layoutRotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(...(layout.rotation ?? [0, 0, 0])));
    const itemState = context.itemParametricStateByKey?.[entry.item.itemKey];
    const normalOffset = new THREE.Vector3(0, itemState?.layoutNormalOffset ?? 0, 0).applyQuaternion(layoutRotation);
    return {
        position: new THREE.Vector3(...layoutPosition).add(normalOffset),
        rotation: layoutRotation.multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(0, angle, 0))),
    };
}
function resolveCrescentItemArcSpan(layout) {
    if (typeof layout.equatorGap === 'number') {
        return Math.max(0, Math.PI - layout.equatorGap * 2);
    }
    return layout.arcSpan ?? Math.PI;
}
function resolveConstellationAnchorWorldTransform(constellation, context) {
    const layout = getConstellationLayout(constellation, context);
    if (!layout)
        return null;
    const { anchor } = layout;
    if (anchor.anchorKind === 'origin') {
        return {
            position: [0, 0, 0],
            yaw: 0,
        };
    }
    if (anchor.anchorKind !== 'item')
        return null;
    if (!anchor.anchorItemKey.startsWith('item:entity:'))
        return null;
    const anchorItemKey = anchor.anchorItemKey;
    const anchorEntry = findEntityItemEntry(anchorItemKey, context);
    if (!anchorEntry)
        return null;
    const anchorTransform = resolveEntityItemAnchorWorldPosition({
        itemKey: anchorItemKey,
        anchorKey: 'center',
        context,
    });
    if (!anchorTransform)
        return null;
    const { constellation: anchorConstellation, items, itemIndex } = anchorEntry;
    const itemCount = items.length;
    if (itemCount === 0)
        return null;
    const anchorLayout = getConstellationLayout(anchorConstellation, context);
    if (anchorLayout?.layoutKind === 'ring' || anchorLayout?.layoutKind === 'sector-ring') {
        const theta = anchorLayout.layoutKind === 'ring'
            ? resolveRingItemAngle({
                layout: anchorLayout,
                itemKey: anchorItemKey,
                itemIndex,
                itemCount,
            })
            : (itemIndex / itemCount) * Math.PI * 2;
        return {
            position: anchorTransform.position,
            yaw: Math.atan2(-Math.sin(theta), Math.cos(theta)),
        };
    }
    return {
        position: anchorTransform.position,
        yaw: 0,
    };
}
function resolveCrescentItemTransform(entry, context) {
    const { constellation, items, itemIndex } = entry;
    const layout = getConstellationLayout(constellation, context);
    if (layout?.layoutKind !== 'crescent')
        return null;
    const itemCount = items.length;
    if (itemCount === 0)
        return null;
    const anchorTransform = resolveConstellationAnchorWorldTransform(constellation, context);
    if (!anchorTransform)
        return null;
    const arcSpan = resolveCrescentItemArcSpan(layout);
    const start = layout.arcCenterAngle - arcSpan / 2;
    const end = layout.arcCenterAngle + arcSpan / 2;
    const angle = itemCount <= 1 ? (start + end) / 2 : start + (itemIndex / (itemCount - 1)) * (end - start);
    const localPosition = new THREE.Vector3(Math.cos(angle) * layout.radius, 0, Math.sin(angle) * layout.radius);
    const crescentRotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(...(layout.rotation ?? [0, 0, 0])));
    localPosition.applyQuaternion(crescentRotation);
    const yawRotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(0, anchorTransform.yaw, 0));
    localPosition.applyQuaternion(yawRotation);
    localPosition.add(new THREE.Vector3(...anchorTransform.position));
    return {
        position: localPosition,
        rotation: yawRotation.multiply(crescentRotation),
    };
}
function resolveSpokeItemTransform(entry, context) {
    const { constellation, itemIndex, item } = entry;
    const layout = getConstellationLayout(constellation, context);
    if (layout?.layoutKind !== 'spoke')
        return null;
    let originNearestPosition = layout.originNearestPosition ?? [0, 0, 0];
    if (layout.anchor.anchorKind === 'parametric') {
        const resolvedPosition = resolveLayoutAnchorWorldPosition({
            anchor: layout.anchor,
            context,
        });
        if (resolvedPosition) {
            originNearestPosition = resolvedPosition;
        }
    }
    const directionUnit = normalizeDirection(layout.direction);
    const spokeRotation = new THREE.Quaternion().setFromEuler(new THREE.Euler(...(layout.rotation ?? [0, 0, 0])));
    const shape = getItemShape(item, context);
    const panelOrientation = (() => {
        if (shape?.shapeKind !== 'panel') {
            return new THREE.Quaternion();
        }
        const planarLength = Math.hypot(directionUnit.x, directionUnit.z);
        if (!Number.isFinite(planarLength) || planarLength <= 1e-8) {
            return new THREE.Quaternion();
        }
        const panelYaw = Math.atan2(directionUnit.x / planarLength, directionUnit.z / planarLength);
        return new THREE.Quaternion().setFromEuler(new THREE.Euler(0, panelYaw, 0));
    })();
    const offsets = resolveSpokeItemOffsets({
        itemCount: entry.items.length, spacing: layout.spacing, itemOffsets: layout.itemOffsets,
    });
    const offset = directionUnit.multiplyScalar(offsets[itemIndex] ?? layout.spacing * itemIndex);
    offset.applyQuaternion(spokeRotation);
    return {
        position: offset.add(new THREE.Vector3(...originNearestPosition)),
        rotation: spokeRotation.clone().multiply(panelOrientation),
    };
}
function resolveConcentricRingItemTransform(entry, context) {
    const { constellation } = entry;
    const layout = getConstellationLayout(constellation, context);
    if (layout?.layoutKind !== 'concentric-ring')
        return null;
    if (layout.anchor.anchorKind !== 'origin')
        return null;
    return {
        position: new THREE.Vector3(0, 0, 0),
        rotation: new THREE.Quaternion().setFromEuler(new THREE.Euler(...(layout.rotation ?? [0, 0, 0]))),
    };
}
export function resolveEntityItemWorldTransform(args) {
    const entry = findEntityItemEntry(args.itemKey, args.context);
    if (!entry)
        return null;
    const transform = resolveRingItemTransform(entry, args.context)
        ?? resolveSectorRingItemTransform(entry, args.context)
        ?? resolveCrescentItemTransform(entry, args.context)
        ?? resolveConcentricRingItemTransform(entry, args.context)
        ?? resolveSpokeItemTransform(entry, args.context)
        ?? null;
    if (!transform)
        return null;
    const position = args.context.itemParametricStateByKey?.[args.itemKey]?.position;
    if (position === undefined)
        return transform;
    if (!Array.isArray(position) || position.length !== 3 || !position.every(Number.isFinite)) {
        throw new Error(`Entity item ${args.itemKey} has an invalid world-space position.`);
    }
    return { ...transform, position: new THREE.Vector3(...position) };
}
export function resolveEntityItemAnchorWorldPosition(args) {
    const { itemKey, anchorKey, context } = args;
    const entry = findEntityItemEntry(itemKey, context);
    if (!entry)
        return null;
    const itemTransform = resolveEntityItemWorldTransform({ itemKey, context });
    if (!itemTransform)
        return null;
    const localAnchor = resolveItemLocalAnchor({
        item: entry.item,
        constellation: entry.constellation,
        itemCount: entry.items.length,
        anchorKey,
        context,
    });
    if (!localAnchor)
        return null;
    const worldPosition = localAnchor.applyQuaternion(itemTransform.rotation).add(itemTransform.position);
    return {
        position: toTuple(worldPosition),
    };
}
// ---------------------------------------------------------------------------
// Parametric endpoint resolution
// ---------------------------------------------------------------------------
function resolveRingIntersectionEndpoint(args) {
    const { endpoint, context } = args;
    // Locate the ring-item entry — determines which constellation and which ring index.
    const ringEntry = findEntityItemEntry(endpoint.ringItemKey, context);
    if (!ringEntry)
        return null;
    const { constellation, items, itemIndex } = ringEntry;
    const layout = getConstellationLayout(constellation, context);
    if (!layout)
        return null;
    // Determine ring radius from layout kind.
    let ringRadius;
    if (layout.layoutKind === 'ring') {
        ringRadius = layout.radius;
    }
    else if (layout.layoutKind === 'concentric-ring') {
        ringRadius = layout.ringRadii?.[itemIndex]
            ?? (layout.startRadius + itemIndex * layout.ringGap);
    }
    else if (layout.layoutKind === 'sector-ring') {
        ringRadius = layout.outerRadius;
    }
    else {
        return null; // Only ring-like layouts are supported.
    }
    const layoutPosition = resolveLayoutAnchorWorldPosition({
        anchor: layout.anchor,
        context,
    });
    if (layoutPosition === null)
        return null;
    const [centerX, baseCenterY, centerZ] = layoutPosition;
    const centerY = baseCenterY + (endpoint.yOffset ?? 0);
    // Resolve angle from policy.
    let angle;
    const { anglePolicy } = endpoint;
    if (anglePolicy.anglePolicyKind === 'explicit') {
        angle = anglePolicy.angle;
    }
    else {
        // match-item-in-ring: derive angle from the matched item's index in its own ring.
        const matchEntry = findEntityItemEntry(anglePolicy.matchItemKey, context);
        if (!matchEntry)
            return null;
        const matchItemCount = matchEntry.items.length;
        if (matchItemCount === 0)
            return null;
        const matchLayout = getConstellationLayout(matchEntry.constellation, context);
        angle = matchLayout?.layoutKind === 'ring'
            ? resolveRingItemAngle({
                layout: matchLayout,
                itemKey: anglePolicy.matchItemKey,
                itemIndex: matchEntry.itemIndex,
                itemCount: matchItemCount,
            })
            : (matchEntry.itemIndex / matchItemCount) * Math.PI * 2;
    }
    // Apply optional radial offset.
    const r = ringRadius + (endpoint.radialOffset ?? 0);
    const resolvedAngle = layout.layoutKind === 'sector-ring'
        ? angle - (layout.rotation?.[1] ?? 0)
        : angle;
    if (layout.layoutKind === 'ring' && layout.rotation) {
        const offset = new THREE.Vector3(Math.cos(angle) * r, 0, Math.sin(angle) * r)
            .applyEuler(new THREE.Euler(...layout.rotation));
        return { position: [centerX + offset.x, centerY + offset.y, centerZ + offset.z] };
    }
    return {
        position: [
            centerX + Math.cos(resolvedAngle) * r,
            centerY,
            centerZ + Math.sin(resolvedAngle) * r,
        ],
    };
}
/**
 * Unified connector endpoint resolver. Dispatches by `endpointKind`:
 * - `'static'`: delegates to `resolveEntityItemAnchorWorldPosition`.
 * - `'parametric'`: resolves by parametric family.
 *
 * Returns
ull` when resolution fails (missing item, unsupported layout, etc.);
 * callers should skip the connector segment in that case.
 */
export function resolveConnectorEndpoint(args) {
    if (args.positionOverride !== undefined) {
        if (!args.positionOverride.every(Number.isFinite)) {
            throw new Error('Connector endpoint position override must contain finite coordinates.');
        }
        return { position: args.positionOverride };
    }
    const { endpoint, context } = args;
    if (endpoint.endpointKind === 'static') {
        return resolveEntityItemAnchorWorldPosition({
            itemKey: endpoint.itemKey,
            anchorKey: endpoint.anchorKey,
            context,
        });
    }
    if (endpoint.endpointKind === 'parametric') {
        if (endpoint.parametricKind === 'ring-intersection') {
            return resolveRingIntersectionEndpoint({ endpoint, context });
        }
    }
    return null;
}
/**
 * Unified layout anchor resolver. Resolves any layout anchor kind to a world position:
 * - `'origin'`: returns [0, 0, 0]
 * - `'item'`: delegates to resolveEntityItemAnchorWorldPosition
 * - `'parametric'`: resolves by parametric family (ring-intersection, etc.)
 *
 * Returns
ull` when resolution fails (missing item, unsupported parametric kind, etc.);
 * callers should handle fallback or use default position.
 */
export function resolveLayoutAnchorWorldPosition(args) {
    const { anchor, context } = args;
    if (anchor.anchorKind === 'origin') {
        return [0, 0, 0];
    }
    if (anchor.anchorKind === 'position') {
        return anchor.position;
    }
    if (anchor.anchorKind === 'item') {
        const result = resolveEntityItemAnchorWorldPosition({
            itemKey: anchor.anchorItemKey,
            anchorKey: 'center',
            context,
        });
        return result?.position ?? null;
    }
    if (anchor.anchorKind === 'parametric') {
        if (anchor.parametricKind === 'ring-intersection') {
            // Convert layout anchor to endpoint ref by changing anchorKind to endpointKind
            // Only include optional fields if they are defined
            const endpoint = {
                endpointKind: 'parametric',
                parametricKind: 'ring-intersection',
                ringItemKey: anchor.ringItemKey,
                anglePolicy: anchor.anglePolicy,
                ...(anchor.yOffset !== undefined ? { yOffset: anchor.yOffset } : {}),
                ...(anchor.radialOffset !== undefined ? { radialOffset: anchor.radialOffset } : {}),
            };
            const result = resolveRingIntersectionEndpoint({
                endpoint,
                context,
            });
            return result?.position ?? null;
        }
    }
    return null;
}
